import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Activity,
  Zap,
  Dog,
  Plus,
  Search,
  Loader2,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Catalog = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newDog, setNewDog] = useState({
    name: '',
    breed: '',
    description: '',
    size: 'medium',
    energy_level: 'moderate',
    temperament: 'friendly',
    image_url: '',
    tags: []
  });

  useEffect(() => {
    fetchDogs();
  }, [sizeFilter]);

  const fetchDogs = async () => {
    setLoading(true);
    try {
      const url = sizeFilter === 'all' ? `${API}/dogs` : `${API}/dogs?size=${sizeFilter}`;
      const response = await axios.get(url);
      setDogs(response.data);
    } catch (error) {
      console.error('Error fetching dogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchDogs();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/search/dogs?q=${searchQuery}`);
      setDogs(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDog = async () => {
    if (!newDog.name.trim() || !newDog.description.trim()) {
      toast.error('Please fill in required fields');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/dogs`, newDog);
      setDogs([response.data, ...dogs]);
      setNewDog({
        name: '',
        breed: '',
        description: '',
        size: 'medium',
        energy_level: 'moderate',
        temperament: 'friendly',
        image_url: '',
        tags: []
      });
      setCreateOpen(false);
      toast.success('Dog added to catalog!');
    } catch (error) {
      toast.error('Failed to add dog');
    } finally {
      setCreating(false);
    }
  };

  const sizes = [
    { value: 'all', label: 'All Sizes' },
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'giant', label: 'Giant' }
  ];

  const energyOptions = [
    { value: 'low', label: 'Low Energy' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'high', label: 'High Energy' }
  ];

  const temperamentOptions = [
    { value: 'friendly', label: 'Friendly' },
    { value: 'calm', label: 'Calm' },
    { value: 'playful', label: 'Playful' },
    { value: 'protective', label: 'Protective' },
    { value: 'independent', label: 'Independent' }
  ];

  const getSizeColor = (size) => {
    const colors = {
      'small': 'bg-blue-100 text-blue-700',
      'medium': 'bg-green-100 text-green-700',
      'large': 'bg-orange-100 text-orange-700',
      'giant': 'bg-purple-100 text-purple-700'
    };
    return colors[size] || 'bg-muted text-muted-foreground';
  };

  const getEnergyIcon = (energy) => {
    const intensity = { low: 1, moderate: 2, high: 3 };
    return intensity[energy] || 2;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-heading font-medium mb-2">Dog Catalog</h1>
            <p className="text-muted-foreground text-lg">
              Discover and document your beloved pets
            </p>
          </div>

          {user && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-dog-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Dog
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add to Catalog</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Dog Name *</Label>
                      <Input
                        placeholder="e.g., Max"
                        value={newDog.name}
                        onChange={(e) => setNewDog({ ...newDog, name: e.target.value })}
                        data-testid="dog-name-input"
                        className="input-field"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Breed</Label>
                      <Input
                        placeholder="e.g., Golden Retriever"
                        value={newDog.breed}
                        onChange={(e) => setNewDog({ ...newDog, breed: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      placeholder="Tell us about this dog..."
                      value={newDog.description}
                      onChange={(e) => setNewDog({ ...newDog, description: e.target.value })}
                      rows={3}
                      data-testid="dog-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Size</Label>
                      <Select
                        value={newDog.size}
                        onValueChange={(val) => setNewDog({ ...newDog, size: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">Small</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="large">Large</SelectItem>
                          <SelectItem value="giant">Giant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Energy</Label>
                      <Select
                        value={newDog.energy_level}
                        onValueChange={(val) => setNewDog({ ...newDog, energy_level: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {energyOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Temperament</Label>
                      <Select
                        value={newDog.temperament}
                        onValueChange={(val) => setNewDog({ ...newDog, temperament: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {temperamentOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Image URL</Label>
                    <Input
                      placeholder="https://..."
                      value={newDog.image_url}
                      onChange={(e) => setNewDog({ ...newDog, image_url: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <Button 
                    className="w-full btn-primary"
                    onClick={handleCreateDog}
                    disabled={creating}
                    data-testid="submit-dog-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Add Dog
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search dogs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 input-field"
              data-testid="dog-search-input"
            />
          </div>
          <Select value={sizeFilter} onValueChange={setSizeFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="size-filter">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              {sizes.map(size => (
                <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Dogs Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : dogs.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Dog className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl mb-2">No dogs found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'Try a different search term' : 'Start adding dogs to your catalog!'}
              </p>
              {user && !searchQuery && (
                <Button className="btn-primary" onClick={() => setCreateOpen(true)}>
                  Add Your First Dog
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {dogs.map((dog) => (
              <Card 
                key={dog.id}
                className="card-interactive group overflow-hidden"
                data-testid={`dog-card-${dog.id}`}
              >
                <div className="aspect-square bg-muted relative overflow-hidden">
                  {dog.image_url ? (
                    <img 
                      src={dog.image_url}
                      alt={dog.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Dog className="w-16 h-16 text-muted-foreground/30" />
                    </div>
                  )}
                  <Badge className={`absolute top-3 right-3 ${getSizeColor(dog.size)}`}>
                    {dog.size}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-heading text-lg font-medium mb-1">{dog.name}</h3>
                  {dog.breed && (
                    <p className="text-sm text-muted-foreground italic mb-2">{dog.breed}</p>
                  )}
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-4">{dog.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground" title="Energy Level">
                      {[...Array(getEnergyIcon(dog.energy_level))].map((_, i) => (
                        <Zap key={i} className="w-3 h-3 text-warning" />
                      ))}
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground" title="Temperament">
                      <Heart className="w-4 h-4 text-secondary" />
                      <span className="capitalize">{dog.temperament}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalog;
